import { MapPinned } from 'lucide-react';
import { lazy, Suspense } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormErrorMessage } from '@/components/form/form-error-message';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ProductionPlaceGeoJsonImport } from '@/features/production-declaration/components/statement/production-place/production-place-geojson-import';
import {
  PRODUCTION_PLACE_MAPTILER_TILE_PROVIDERS,
  type ProductionPlaceMapTilerTileProviderKey,
} from '@/features/production-declaration/components/statement/production-place/production-place-maptiler-providers';
import type { ProductionPlaceFormValues } from '@/features/production-declaration/schema/production-place-schema';

const ProductionPlaceVectorMapField = lazy(() =>
  import(
    '@/features/production-declaration/components/statement/production-place/production-place-vector-map-field'
  ).then((module) => ({ default: module.ProductionPlaceVectorMapField })),
);

const productionPlaceMapFallback = (
  <Skeleton className='min-h-[28rem] w-full rounded-[1.4rem] lg:min-h-[34rem]' />
);

type ProductionPlaceMapSectionProps = {
  control: Control<ProductionPlaceFormValues>;
  disabled: boolean;
  formLabelClassName: string;
  mapStyleId: string;
  onTileProviderChange: (value: string) => void;
  onImportPendingChange: (pending: boolean) => void;
  tileProviderKey: ProductionPlaceMapTilerTileProviderKey;
};

export function ProductionPlaceMapSection({
  control,
  disabled,
  formLabelClassName,
  mapStyleId,
  onTileProviderChange,
  onImportPendingChange,
  tileProviderKey,
}: ProductionPlaceMapSectionProps) {
  const { t } = useTranslation('productionDeclaration');

  return (
    <div className='space-y-2'>
      <Controller
        control={control}
        name='coordinates'
        render={({ field, fieldState }) => (
          <>
            <div className='flex flex-col gap-4 sm:flex-row sm:justify-between'>
              <div className='relative z-10 w-full min-w-0 max-w-96 space-y-2 sm:flex-1'>
                <Label
                  className={`h-5 ${formLabelClassName}`}
                  htmlFor={mapStyleId}
                >
                  {t(
                    'materialCommodityOrigins.cards.productionPlace.fields.mapStyle',
                  )}
                </Label>
                <Select
                  disabled={disabled}
                  onValueChange={onTileProviderChange}
                  value={tileProviderKey}
                >
                  <SelectTrigger
                    className='h-12 min-h-12 w-full rounded-lg border-declaration-border bg-white px-4 py-0 text-base text-declaration-foreground shadow-none focus-visible:border-declaration-primary focus-visible:ring-declaration-primary/15 [&_[data-slot=select-value]]:text-base'
                    id={mapStyleId}
                  >
                    <SelectValue
                      placeholder={t(
                        'materialCommodityOrigins.cards.productionPlace.fields.mapStylePlaceholder',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCTION_PLACE_MAPTILER_TILE_PROVIDERS.map(
                      (provider) => (
                        <SelectItem key={provider.key} value={provider.key}>
                          {t(provider.labelKey)}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>
              <ProductionPlaceGeoJsonImport
                disabled={disabled}
                onChange={field.onChange}
                onPendingChange={onImportPendingChange}
                value={field.value}
              />
            </div>
            <Suspense fallback={productionPlaceMapFallback}>
              <ProductionPlaceVectorMapField
                disabled={disabled}
                onChange={field.onChange}
                tileProviderKey={tileProviderKey}
                value={field.value}
              />
            </Suspense>
            <div className='flex items-start gap-3 rounded-xl border border-declaration-border bg-declaration-hover px-4 py-3'>
              <div className='mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border border-declaration-border bg-white text-declaration-muted-foreground'>
                <MapPinned className='size-4' />
              </div>
              <div>
                <p className='font-semibold text-declaration-foreground text-sm'>
                  {t(
                    'materialCommodityOrigins.cards.productionPlace.mapPlaceholderTitle',
                  )}{' '}
                  *
                </p>
                <p className='mt-1 text-declaration-foreground-muted text-sm leading-6'>
                  {t(
                    'materialCommodityOrigins.cards.productionPlace.mapPlaceholderDescription',
                  )}
                </p>
              </div>
            </div>
            <FormErrorMessage
              error={fieldState.error?.message}
              ns='productionDeclaration'
            />
          </>
        )}
      />
    </div>
  );
}
